import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ROOMS_DATA } from "@/data/rooms";
import { Room } from "@/types/room";
import { Calendar, Mountain, Star } from "lucide-react";
import ContactConciergeModal from "@/components/ContactConciergeModal";
import NewHeader from "@/components/NewHeader";
import SEO from "@/components/SEO";
import SpaRenovationRibbon from "@/components/spa/SpaRenovationRibbon";
import { createBreadcrumbSchema, createVideoSchema } from "@/data/structuredData";
import FamilySuiteSection from "@/components/FamilySuiteSection";
import JuniorSuiteSection from "@/components/JuniorSuiteSection";
import TripleRoomSection from "@/components/TripleRoomSection";
import DoubleRoomSection from "@/components/DoubleRoomSection";
import AlpineDoubleTwinSection from "@/components/AlpineDoubleTwinSection";
import AlpineClassicDoubleTwinSection from "@/components/AlpineClassicDoubleTwinSection";
import JungfrauSingleSection from "@/components/JungfrauSingleSection";
import HeritageSingleSection from "@/components/HeritageSingleSection";
import SingleRoomSection from "@/components/SingleRoomSection";
import Footer from "@/components/Footer";
import Testimonials from "@/components/Testimonials";
import AccommodationsFAQ from "@/components/AccommodationsFAQ";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPageSEO, SEOLanguage } from "@/data/seoTranslations";
import { getTranslatedStructuredData } from "@/data/structuredDataTranslations";
import RatingDisplay from "@/components/RatingDisplay";

// Removed unused VIEW_ICONS and VIEW_LABELS

const Accommodations: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const seoLang = language as SEOLanguage;
  const seoData = getPageSEO("accommodations", seoLang);
  // Breadcrumb schema
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: "Home", url: "https://beatenberg-resortspa.ch/" },
    {
      name: "Accommodations",
      url: "https://beatenberg-resortspa.ch/accommodations",
    },
  ]);

  // Video schema for hero video
  const videoSchema = createVideoSchema({
    name: "Beatenberg Resort & Spa - Luxury Accommodations",
    description: "Experience our elegant rooms and suites with panoramic Alpine views at Beatenberg Resort & Spa",
    videoUrl: "https://beatenberg-resortspa.ch/videos/accommodations-hero-720p-optimized.mp4",
    thumbnailUrl: "https://beatenberg-resortspa.ch/images/Views/accommodations-hero-thumb.webp",
    uploadDate: "2025-01-15",
    duration: "PT30S"
  });

  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  // Removed filtered rooms state
  // Removed featured slider state

  // Removed featured rooms slider data
  const allRooms = ROOMS_DATA;

  // Safari video autoplay fix - Programmatic play trigger
  useEffect(() => {
    const playVideo = async () => {
      if (heroVideoRef.current) {
        try {
          await heroVideoRef.current.play();
        } catch (error) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try to play when component mounts
    playVideo();

    // Also try when video metadata is loaded
    const video = heroVideoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      return () => video.removeEventListener("loadedmetadata", playVideo);
    }
  }, []);

  // Handle deep linking to sections
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          // Add a small delay to ensure all components are loaded
          setTimeout(() => {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 1000); // Longer delay for accommodations page due to lazy loading
        }
      }
    };

    // Handle initial load with hash
    handleHashChange();

    // Handle hash changes
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Removed featured rooms list memo

  // Removed filter change handler

  const handleRoomSelect = (roomId: string) => {
    const room = ROOMS_DATA.find((r) => r.id === roomId);
    if (room) {
      setSelectedRoom(room);
      // Scroll to room details section instead of navigating
      const roomSection = document.getElementById(`room-${roomId}`);
      roomSection?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleQuickBook = (roomId: string) => {
    const room = ROOMS_DATA.find((r) => r.id === roomId);
    if (room) {
      // Open booking widget or modal
      console.log("Quick booking for room:", room.name);
      // Scroll to booking section
      const bookingSection = document.getElementById("booking-section");
      bookingSection?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleExploreRooms = () => {
    // Smooth scroll to horizontal showcase
    const firstSection = document.getElementById("first-section");
    firstSection?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBookDirect = () => {
    // Open booking widget or navigate to booking page
    console.log("Open direct booking");
  };

  // Removed slider navigation functions

  // Removed scroll detection for slider section

  return (
    <div className="min-h-screen bg-white" data-oid="7kvyev4">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical="/accommodations"
        language={seoLang}
        structuredData={[
          getTranslatedStructuredData.accommodations(seoLang),
          getTranslatedStructuredData.accommodationsFAQ(seoLang),
          breadcrumbSchema,
          videoSchema,
        ]}
        data-oid="8q4:57c"
      />

      {/* Video Preload Links for Hero Section */}
      <Helmet>
        <link
          rel="preload"
          as="video"
          href="/videos/accommodations-hero-720p-optimized.mp4"
          media="(min-width: 769px)"
        />
        <link
          rel="preload"
          as="video"
          href="/videos/accommodations-hero-mobile-ultra-optimized.mp4"
          media="(max-width: 768px)"
        />
      </Helmet>

      <NewHeader data-oid="0ua2-l." />
      {/* <SpaRenovationRibbon /> */}
      {/* Skip Link for Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-alpine-gold text-white px-4 py-2 rounded-md z-50 focus:outline-none focus:ring-2 focus:ring-alpine-gold focus:ring-offset-2"
        data-oid="jroq0d6"
      >
        Skip to main content
      </a>

      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden"
        data-oid="0ed8cfd"
      >
        {/* Background Video */}
        <div className="absolute inset-0" data-oid="or3sjyp">
          <video
            ref={heroVideoRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            fetchPriority="high"
            onLoadedData={(e) => {
              e.currentTarget.currentTime = 3;
            }}
            data-oid="e.e8rry"
          >
            <source
              src="/videos/accommodations-hero-720p-optimized.mp4"
              type="video/mp4"
              media="(min-width: 769px)"
              data-oid="9e:b:5s"
            />

            <source
              src="/videos/accommodations-hero-mobile-ultra-optimized.mp4"
              type="video/mp4"
              media="(max-width: 768px)"
              data-oid="0mvjymp"
            />

            {/* Fallback image if video doesn't load */}
            <div
              className="absolute inset-0 bg-gradient-to-br from-alpine-gold/20 via-lake-blue/30 to-pine-green/20"
              data-oid="l-7f3fb"
            >
              <div
                className="absolute inset-0 bg-[url('/images/Views/')] bg-cover bg-center opacity-40"
                data-oid="91.p_1y"
              />
            </div>
          </video>

          {/* Fade Overlay for smoother transitions */}
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 z-5"
            data-oid="38kan.:"
          />

          {/* Light dark overlay for text readability */}
          <div
            className="absolute inset-0 bg-black/15 z-10"
            data-oid="o9i-3xy"
          />
        </div>

        {/* Hero Content */}
        <div
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
          data-oid=":1v7abo"
        >
          <Badge
            variant="secondary"
            className="mb-6 bg-white/90 text-alpine-gold border-0 backdrop-blur-sm"
            data-oid="dpzfp61"
          >
            <Star className="w-4 h-4 mr-2" data-oid="4rx405y" />
            {t("hero.since1881")} • {t("hero.swissHeritageHotel")}
          </Badge>

          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-playfair text-white mb-6 leading-tight"
            data-oid="r5.7bg6"
          >
            {t("rooms.title")}
            <span className="block text-alpine-gold" data-oid="uxkfekm">
              {t("hero.stunningAlpineViews")}
            </span>
          </h1>

          <p
            className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed"
            data-oid="wkjbhy6"
          >
            {t("rooms.description")}
          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            data-oid="oqp.rq9"
          >
            <Button
              size="lg"
              className="btn-alpine text-lg px-8 py-4 h-auto rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              onClick={handleExploreRooms}
              data-oid="h_.cwd_"
            >
              <Mountain className="w-5 h-5 mr-2" data-oid="uqrljoo" />
              {t("rooms.exploreRooms")}
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 text-lg px-8 py-4 h-auto rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              trackingType="booking"
              trackingLabel={t("nav.bookNow")}
              trackingLocation="Accommodations Hero"
              data-oid="fkvjxd1"
            >
              <a
                href={import.meta.env.VITE_BOOKING_URL}
                data-oid="tm4ed63"
              >
                <Calendar className="w-5 h-5 mr-2" data-oid="-c.s7-g" />
                {t("nav.bookNow")}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Anchor: First content section for hero "Explore Rooms" scroll */}
      <div id="first-section" data-oid="avp:qlq" />

      {/* Family Suite Section */}
      <FamilySuiteSection
        room={ROOMS_DATA.find((room) => room.id === "family-suite")!}
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="p:9nle7"
      />

      {/* Junior Suite Section */}
      <JuniorSuiteSection
        room={ROOMS_DATA.find((room) => room.id === "junior-suite")!}
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="ofyx:o-"
      />

      {/* Triple Room Section */}
      <TripleRoomSection
        room={ROOMS_DATA.find((room) => room.id === "standard-triple-room")!}
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="79zvg0l"
      />

      {/* Double Room Section */}
      <DoubleRoomSection
        room={ROOMS_DATA.find((room) => room.id === "superior-double-room")!}
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="5iz5l:v"
      />

      {/* Alpine Double/Twin Section */}
      <AlpineDoubleTwinSection
        room={
          ROOMS_DATA.find(
            (room) => room.id === "alpine-double-twin-balcony-lake",
          )!
        }
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="8x1i7ty"
      />

      {/* Alpine Classic Double/Twin Section */}
      <AlpineClassicDoubleTwinSection
        room={
          ROOMS_DATA.find(
            (room) => room.id === "alpine-classic-double-twin-village",
          )!
        }
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="idfelsq"
      />

      {/* Jungfrau Single Section */}
      <JungfrauSingleSection
        room={
          ROOMS_DATA.find((room) => room.id === "jungfrau-single-balcony-lake")!
        }
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="2jhphdp"
      />

      {/* Heritage Single Section */}
      <HeritageSingleSection
        room={ROOMS_DATA.find((room) => room.id === "heritage-single-village")!}
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="p:4gogt"
      />

      {/* Single Room Section */}
      <SingleRoomSection
        room={ROOMS_DATA.find((room) => room.id === "standard-single")!}
        onViewDetails={handleRoomSelect}
        onQuickBook={handleQuickBook}
        data-oid="bhjq9jz"
      />

      {/* CTA Section with Filters (Cozy content incorporated) */}
      <section
        id="main-content"
        className="py-16 bg-gradient-to-b from-white to-gray-50/50"
        data-oid="lqgu3c."
      >
        <div className="max-w-7xl mx-auto px-4" data-oid="8:dbiza">
          {/* Section Header (CTA) */}
          <div className="text-center mb-12" data-oid="a0:q8dr">
            <h2
              className="text-3xl md:text-4xl font-playfair text-gray-900 mb-4"
              data-oid=":s:ofxe"
            >
              {t("cta.title")}
            </h2>
            <p
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-oid="9a:pzx."
            >
              {t("cta.description")}
            </p>
          </div>

          {/* Filter removed as requested */}
        </div>

        {/* Call to Action Buttons */}
        <div
          className="max-w-4xl mx-auto text-center px-4 mt-16"
          data-oid="_5z8w1r"
        >
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            data-oid="5b:i-up"
          >
            <Button
              asChild
              size="lg"
              className="btn-alpine rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              trackingType="booking"
              trackingLabel={t("cta.startReservation")}
              trackingLocation="Accommodations Page CTA"
              data-oid="pgxec5z"
            >
              <a
                href={import.meta.env.VITE_BOOKING_URL}
                className="w-full sm:w-auto"
                data-oid=".8-dtba"
              >
                <Calendar className="w-5 h-5 mr-2" data-oid="77_ry-h" />
                {t("cta.startReservation")}
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-gray-300 hover:border-alpine-gold hover:text-alpine-gold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              onClick={() => setIsContactModalOpen(true)}
              trackingType="contact_modal"
              trackingLabel={t("cta.contactTeam")}
              trackingLocation="Accommodations Page CTA"
              trackingData={{ modalType: "concierge" }}
              data-oid="j6yjtse"
            >
              {t("cta.contactTeam")}
            </Button>
          </div>

          {/* Rating Display */}
          <div className="mt-8 flex justify-center" data-oid="85ffxu7">
            <RatingDisplay
              rating={4.2}
              reviewCount={245}
              type="hotel"
              theme="dark"
              className="justify-center"
              data-oid="z0rtjq-"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <AccommodationsFAQ data-oid="p-vlo_3" />

      {/* Guest Experiences Section */}
      <Testimonials data-oid="0yvy8tu" />

      {/* Footer */}
      <Footer data-oid="f.qaacp" />

      {/* Bottom Dock Navigation */}
      {/* <BottomDockNavigation /> */}

      {/* Contact Concierge Modal */}
      <ContactConciergeModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        data-oid="0mog-ct"
      />
    </div>
  );
};

export default Accommodations;
